import { useEffect, useState } from "react";
import { Users, Terminal, Shield, Blocks, MessageSquare, Ban, AlertTriangle, VolumeX } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { StatCard, PageHead } from "@/components/kit";
import { MyStatsCard } from "@/components/MyStatsCard";

const actionIcon = { ban: Ban, warn: AlertTriangle, mute: VolumeX, unban: Shield, kick: Ban };
const actionColor = { ban: "#ef4444", warn: "#f97316", mute: "#eab308", unban: "#10b981", kick: "#ef4444" };

export default function Overview() {
  const { guild, guildId, refreshKey } = useGuild();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/guilds/${guildId}/overview`).then((r) => setData(r.data)); }, [guildId, refreshKey]);

  if (!data) return <p className="text-zinc-500">Loading overview…</p>;
  const s = data.stats;

  return (
    <div>
      <PageHead title="Server Overview" desc="Real-time snapshot of your community." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Members" value={s.members.toLocaleString()} />
        <StatCard icon={MessageSquare} label="Messages (30d)" value={s.total_messages.toLocaleString()} accent="#f59e0b" />
        <StatCard icon={Terminal} label="Commands" value={s.commands} accent="#facc15" />
        <StatCard icon={Blocks} label="Active Plugins" value={s.active_plugins} accent="#10b981" />
      </div>
      <MyStatsCard guild={guild} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Activity — last 14 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.activity}>
              <defs><linearGradient id="msg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.4} /><stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#94a3b8" strokeOpacity={0.2} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "rgb(var(--z-900))", border: "1px solid rgb(var(--z-800))", borderRadius: 8 }} />
              <Area type="monotone" dataKey="messages" stroke="#0EA5E9" strokeWidth={2} fill="url(#msg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Top Members</h3>
          <div className="space-y-3">
            {data.top_members.map((m, i) => (
              <div key={m.id} className="flex items-center gap-3">
                <span className="text-zinc-600 font-mono text-sm w-4">{i + 1}</span>
                <img src={m.avatar} className="w-8 h-8 rounded-full bg-zinc-800" alt="" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{m.username}</div><div className="text-xs text-zinc-500">Lvl {m.level}</div></div>
                <span className="text-xs font-mono text-volt">{m.xp.toLocaleString()} XP</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6">
        <h3 className="font-head font-semibold mb-4">Recent Moderation</h3>
        <div className="space-y-2">
          {data.recent_actions.map((a) => {
            const Icon = actionIcon[a.action] || Shield;
            return (
              <div key={a.id} className="flex items-center gap-3 border-l-2 pl-3 py-1.5" style={{ borderColor: actionColor[a.action] }}>
                <Icon className="w-4 h-4" style={{ color: actionColor[a.action] }} />
                <span className="text-sm font-medium capitalize" style={{ color: actionColor[a.action] }}>{a.action}</span>
                <span className="text-sm text-zinc-300">{a.target}</span>
                <span className="text-sm text-zinc-500">· {a.reason}</span>
                <span className="text-xs text-zinc-600 ml-auto">by {a.moderator}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
